import { CalendarState, Dimensions, Placement, SpiralItem } from "burde";
import * as d3 from "d3";
import { BaseType } from "d3";
import { BleedSize, CalendarDimensions } from "svg/constants";
import { LayoutFont } from "ui/constants";
import { getNumberOfSpiralsFromCalendarSize } from "ui/helper";
import { D3Selection } from "../draw-calendar";
import { drawCoverSpiral } from "../draw-cover-spiral";
import { drawDualPageSpiral } from "../draw-dual-page-spiral";
import { drawSvgDropShadow } from "../draw-svg-dropshadow";

export const getSinglePagePreviewContainer = (
    baseContainerRef: HTMLDivElement | null,
    calendar: CalendarState,
    spiralSettings: {
        active: boolean;
        positionToRight: boolean;
    },
    spiral: SpiralItem | null,
) => {
    if (!baseContainerRef) return;

    const baseType: BaseType = baseContainerRef;
    let container = d3.select(baseType);
    container.html("");

    let svg = container.append("svg") as D3Selection;

    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendar.calendarSize,
    );

    if (calendarDimensions == undefined) return;

    let shadowDimensions: Dimensions = {
        Width: calendarDimensions.contentWidthWithoutBleeds,
        Height: calendarDimensions.contentHeightWithoutBleeds,
    };

    let shadowPlacement: Placement = {
        X: calendarDimensions.presentationEmptySpaceWidth,
        Y: calendarDimensions.presentationEmptySpaceHeight,
    };

    drawSvgDropShadow(svg, shadowDimensions, shadowPlacement);

    svg.attr("preserveAspectRatio", "xMidYMid meet")
        .attr(
            "viewBox",
            `0 0 ${
                calendarDimensions.contentWidthWithoutBleeds +
                calendarDimensions.presentationEmptySpaceWidth * 2
            } ${
                calendarDimensions.contentHeightWithoutBleeds +
                calendarDimensions.presentationEmptySpaceHeight * 2
            }`,
        )
        .classed("svg-content", true)
        .classed("coverPreview", true)
        .style("height", "100%")
        .style("width", "100%")
        .style("margin", "0 auto");

    const coverSvg = svg
        .append("g")
        .attr(
            "transform",
            `translate(${calendarDimensions.presentationEmptySpaceWidth},${calendarDimensions.presentationEmptySpaceHeight})`,
        )
        .attr("id", "coverSvg")
        .append("svg")
        .attr("width", calendarDimensions.contentWidthWithoutBleeds)
        .attr("height", calendarDimensions.contentHeightWithoutBleeds)
        .attr(
            "viewBox",
            `${BleedSize} ${BleedSize} ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
        )
        .classed("leftPageSvg", true);

    var background = coverSvg.append("g").classed("background-content", true);

    background
        .append("rect")
        .attr("width", calendarDimensions.svgWidth)
        .attr("height", calendarDimensions.svgHeight)
        .attr("fill", "#FFFFFF");

    if (spiralSettings.active) {
        addSinglePageSpiral(
            svg,
            calendar,
            spiralSettings.positionToRight,
            spiral,
        );
    }

    return coverSvg;
};

export const addSinglePageSpiral = (
    svg: D3Selection,
    calendar: CalendarState,
    positionToRight: boolean,
    spiral: SpiralItem | null,
) => {
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendar.calendarSize,
    );

    if (calendarDimensions == undefined) return;

    const numberOfSpirals = getNumberOfSpiralsFromCalendarSize(
        calendar.calendarSize,
    );

    drawCoverSpiral(
        svg,
        spiral,
        positionToRight,
        numberOfSpirals,
        calendarDimensions,
    );
};

export const addDualPageSpiral = (
    svg: D3Selection,
    calendar: CalendarState,
    spiral: SpiralItem | null,
) => {
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendar.calendarSize,
    );

    const numberOfSpirals = getNumberOfSpiralsFromCalendarSize(
        calendar.calendarSize,
    );

    if (calendarDimensions == undefined) return;

    drawDualPageSpiral(svg, spiral, numberOfSpirals, calendarDimensions);
};

export const getDualPagePreviewContainer = (
    baseContainerRef: HTMLDivElement | null,
    calendar: CalendarState,
    spiral: SpiralItem | null,
) => {
    if (!baseContainerRef) return;

    let container = d3.select(baseContainerRef as BaseType);
    container.html("");

    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendar.calendarSize,
    );

    if (calendarDimensions == undefined) return;

    const svg = container
        .append("svg")
        .attr("width", "100%")
        .attr("height", "100%")
        .attr("preserveAspectRatio", "xMidYMid meet")
        .attr(
            "viewBox",
            `0 0 ${
                calendarDimensions.contentWidthWithoutBleeds * 2 +
                calendarDimensions.pageGap +
                calendarDimensions.presentationEmptySpaceWidth * 2
            } ${
                calendarDimensions.contentHeightWithoutBleeds +
                calendarDimensions.presentationEmptySpaceHeight * 2
            }`,
        )
        .classed("svg-content", true) as D3Selection;

    const leftPageDropShadowDimensions: Dimensions = {
        Width: calendarDimensions.contentWidthWithoutBleeds,
        Height: calendarDimensions.contentHeightWithoutBleeds,
    };

    const leftPageDropShadowPlacement: Placement = {
        X: calendarDimensions.presentationEmptySpaceWidth,
        Y: calendarDimensions.presentationEmptySpaceHeight,
    };

    drawSvgDropShadow(
        svg,
        leftPageDropShadowDimensions,
        leftPageDropShadowPlacement,
    );

    const leftSvgGroup = svg
        .append("g")
        .attr(
            "transform",
            `translate(${calendarDimensions.presentationEmptySpaceWidth},${calendarDimensions.presentationEmptySpaceHeight})`,
        )
        .attr("id", "leftPageGroup");

    const leftSvg = leftSvgGroup
        .append("svg")
        .attr("width", calendarDimensions.contentWidthWithoutBleeds)
        .attr("height", calendarDimensions.contentHeightWithoutBleeds)
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
        )
        .classed("leftPageSvg", true);

    let rightPageDropShadowDimensions: Dimensions = {
        Width: calendarDimensions.contentWidthWithoutBleeds,
        Height: calendarDimensions.contentHeightWithoutBleeds,
    };
    let rightPageDropShadowPlacement: Placement = {
        X:
            calendarDimensions.contentWidthWithoutBleeds +
            calendarDimensions.presentationEmptySpaceWidth +
            calendarDimensions.pageGap,
        Y: calendarDimensions.presentationEmptySpaceHeight,
    };

    drawSvgDropShadow(
        svg,
        rightPageDropShadowDimensions,
        rightPageDropShadowPlacement,
    );

    const rightSvgGroup = svg
        .append("g")
        .attr("id", "rightPageGroup")
        .attr(
            "transform",
            `translate(${
                calendarDimensions.contentWidthWithoutBleeds +
                calendarDimensions.pageGap +
                calendarDimensions.presentationEmptySpaceWidth
            }, ${calendarDimensions.presentationEmptySpaceHeight})`,
        );

    const rightSvg = rightSvgGroup
        .append("svg")
        .attr("width", calendarDimensions.contentWidthWithoutBleeds)
        .attr("height", calendarDimensions.contentHeightWithoutBleeds)
        .attr(
            "viewBox",
            `0 0 ${calendarDimensions.contentWidthWithoutBleeds} ${calendarDimensions.contentHeightWithoutBleeds}`,
        )
        .classed("rightPageSvg", true);

    addDualPageSpiral(svg, calendar, spiral);

    return {
        leftSvg: leftSvg,
        rightSvg: rightSvg,
    };
};

export const applyFontData = (fontData: LayoutFont, base: D3Selection) => {
    // If fontData has a format (eg. is a file and not a Google Font) we need to use a font-face inside a style tag. Otherwise we can just link the style.
    if (fontData.format) {
        const fontFace = `@font-face { font-family: "${fontData["font-family"]}"; src: url("${fontData.url}") format("${fontData.format}");`;
        base.append("style").text(fontFace);
    } else {
        const existingFont = document.querySelector(
            `link[data-font-title="${fontData.title}"]`,
        );
        if (!existingFont) {
            const link = document.createElement("link");
            link.dataset.fontTitle = fontData.title;
            link.href = fontData.url;
            link.rel = "stylesheet";
            document.head.appendChild(link);
        }
    }
};
